import { useCallback, useState } from 'react';
import { toDateKeyTz, toTimeKeyTz } from '@/lib/tz';

/**
 * End-before-start messages (YAR-382).
 *
 * The *behaviour* is settled — YAR-130 AC-11 rules that an inverted range
 * raises an inline error on the End field and blocks the save until it is
 * corrected — but neither AC-11 nor YAR-136/137 spells out the string, and
 * YAR-382 carries a "Needs Design Decision" label with no ruling in its
 * comments. `DATE_ORDER_MESSAGE` is the wording already shipped for the
 * date half; `TIME_ORDER_MESSAGE` is a parallel construction standing in
 * for the same-day time case. Treat both as PENDING FINAL COPY.
 */
export const DATE_ORDER_MESSAGE = 'End date must be after start date.';
export const TIME_ORDER_MESSAGE = 'End time must be after start time.';

export interface DateTimeFieldsInitial {
  /** The stored start (a task's due date when it has no start), or null. */
  start: string | null;
  end: string | null;
  /** `is_time_unknown`: the email stated no hour. */
  allDay: boolean;
  /** Pre-fill for a new item (YYYY-MM-DD); opens all-day. */
  defaultDate?: string;
}

export interface DateTimeOrderErrors {
  dateOrder: boolean;
  timeOrder: boolean;
}

/**
 * The all-day / start / end slice of the item form, shared by
 * `ItemFormModal` and the Review v0 `DateTimeSheet` (YAR-554). Values are
 * the pickers' naive keys ("YYYY-MM-DD", "HH:mm") in `timezone`; turning
 * them back into instants (`toIsoWithTz`) is the caller's job.
 */
export function useDateTimeFields(
  initial: DateTimeFieldsInitial,
  timezone: string | null,
) {
  const [allDay, setAllDay] = useState(() => {
    if (initial.allDay) return true;
    // A bare "YYYY-MM-DD" carries no hour. Tasks reach here with their due
    // date as `start`: without it a date-only legacy task would open with
    // the time picker on and empty, inviting a made-up hour (YAR-376).
    if (initial.start) return initial.start.length <= 10;
    return !!initial.defaultDate;
  });
  const [startDate, setStartDate] = useState(
    () =>
      toDateKeyTz(initial.start ?? '', timezone) || initial.defaultDate || '',
  );
  const [startTime, setStartTime] = useState(() =>
    toTimeKeyTz(initial.start ?? '', timezone),
  );
  const [endDate, setEndDate] = useState(() =>
    toDateKeyTz(initial.end ?? '', timezone),
  );
  const [endTime, setEndTime] = useState(() =>
    toTimeKeyTz(initial.end ?? '', timezone),
  );

  /** A new start date drags an empty (or same-day) end date along. */
  const changeStartDate = useCallback(
    (next: string) => {
      setStartDate(next);
      if (!endDate || endDate === startDate) setEndDate(next);
    },
    [endDate, startDate],
  );

  /** "YYYY-MM-DD" when all-day or no time, else "YYYY-MM-DDTHH:mm". */
  const combineDatetime = useCallback(
    (date: string, time: string): string | null => {
      if (!date) return null;
      if (allDay || !time) return date;
      return `${date}T${time}`;
    },
    [allDay],
  );

  /**
   * An inverted range. Dates are checked for tasks too (a task carries an
   * `end_datetime` of its own), and same-day inverted *times* —
   * "12:00 PM → 7:30 AM" — once passed because only the date halves were
   * compared (YAR-382). Both are lexicographic: "YYYY-MM-DD" and 24h "HH:mm".
   */
  const orderErrors = useCallback(
    (): DateTimeOrderErrors => ({
      dateOrder: !!startDate && !!endDate && endDate < startDate,
      timeOrder:
        !allDay &&
        !!startDate &&
        !!endDate &&
        endDate === startDate &&
        !!startTime &&
        !!endTime &&
        endTime < startTime,
    }),
    [allDay, startDate, endDate, startTime, endTime],
  );

  return {
    allDay,
    setAllDay,
    startDate,
    setStartDate,
    changeStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    combineDatetime,
    orderErrors,
  };
}
